/* Master Order — Себестоимость ингредиентов drawer.
 * Only the ingredient-cost layer of a single recipe/component. No packaging,
 * labor, utilities, overhead, or margin — those live in the full costing flow.
 *
 * Uses:
 * - POST /api/master-order/recipe-cost/resolve   → match & compute
 * - POST /api/master-order/recipe-cost/quick-price → «Указать цену»
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Info, ChevronRight, Pencil } from "lucide-react";
import { resolveRecipeCost, quickSavePrice, patchIngredient } from "@/lib/masterOrderApi";

const CURRENCIES = ["EUR", "USD", "RUB"];
const PURCHASE_UNITS = ["kg", "g", "l", "ml", "pcs"];
const BASE_UNITS = ["g", "ml", "pcs"];

/* ── unit family helpers (mirror backend units.py, mass↔volume↔count only) */
const UNIT_FAMILY = { g: "mass", kg: "mass", ml: "volume", l: "volume", pcs: "count" };
const familyOf = (u) => UNIT_FAMILY[u] || null;
const canonicalRecipeUnit = (u) => (UNIT_FAMILY[u] ? u : null);
/* Recipe unit → suitable base_unit for a NEW ingredient */
const suggestedBaseUnit = (recipeUnit) => {
  const f = familyOf(recipeUnit);
  if (f === "mass") return "g";
  if (f === "volume") return "ml";
  if (f === "count") return "pcs";
  return "g";
};

const fmtMoney = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(2).replace(".", ",");
};
const fmtQty = (n, unit) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const rounded = v >= 10 ? Math.round(v) : Number(v.toFixed(1));
  return `${String(rounded).replace(".", ",")} ${unit || ""}`.trim();
};

/* ── shared primitives (copy of MORecipeRecalc's local styles to keep parity) */
const Label = ({ children }) => (
  <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1.5" style={{ color: "#8B6F4B" }}>{children}</div>
);
const Field = ({ children, testid }) => (
  <div className="w-full h-10 px-3 rounded-md bg-white text-base md:text-[13px] outline-none flex items-center"
       style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
       data-testid={testid}>
    {children}
  </div>
);
const TextInput = ({ value, onChange, placeholder, testid, inputMode }) => (
  <input type="text" inputMode={inputMode || "text"} value={value ?? ""}
         onChange={(e) => onChange(e.target.value)}
         placeholder={placeholder}
         className="w-full h-10 px-3 rounded-md bg-white text-base md:text-[13px] outline-none"
         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
         data-testid={testid} />
);
const NumberInput = ({ value, onChange, placeholder, testid }) => (
  <TextInput inputMode="decimal" value={value}
             onChange={(v) => onChange(v.replace(",", "."))}
             placeholder={placeholder} testid={testid} />
);
const Chip = ({ active, onClick, testid, children }) => (
  <button onClick={onClick}
          className="h-9 px-3 rounded-full text-[12px]"
          style={{
            background: active ? "#4A3728" : "#F7F1E7",
            color: active ? "#F7EFE0" : "#4A3728",
            boxShadow: active ? "none" : "inset 0 0 0 1px #EFE4D0",
          }}
          data-testid={testid}>{children}</button>
);
const PrimaryBtn = ({ onClick, disabled, testid, children }) => (
  <button onClick={onClick} disabled={disabled}
          className="h-11 px-5 rounded-full text-[13px] disabled:opacity-60"
          style={{ background: "#4A3728", color: "#F7EFE0" }}
          data-testid={testid}>{children}</button>
);
const GhostBtn = ({ onClick, testid, children }) => (
  <button onClick={onClick}
          className="h-11 px-4 rounded-full text-[13px]"
          style={{ background: "transparent", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
          data-testid={testid}>{children}</button>
);

/* ─────────── inner: quick-price sub-drawer ─────────── */
function QuickPriceForm({ line, currency, onSaved, onCancel }) {
  const recipeUnitFamily = familyOf(line.unit);
  const defaultBase = line.ingredient_base_unit || suggestedBaseUnit(line.unit);
  const [name, setName] = useState(line.ingredient_name || line.name);
  const [baseUnit, setBaseUnit] = useState(defaultBase);
  const [purchaseQty, setPurchaseQty] = useState("");
  const [purchaseUnit, setPurchaseUnit] = useState(
    recipeUnitFamily === "mass" ? "kg" : recipeUnitFamily === "volume" ? "l" : "pcs"
  );
  const [price, setPrice] = useState("");
  const [supplier, setSupplier] = useState("");
  const [saving, setSaving] = useState(false);

  const isReuse = !!line.ingredient_id && (line.status === "matched" || line.status === "matched_no_price");

  const purchaseUnitOptions = useMemo(() => {
    if (baseUnit === "g") return ["kg", "g"];
    if (baseUnit === "ml") return ["l", "ml"];
    return ["pcs"];
  }, [baseUnit]);

  useEffect(() => {
    if (!purchaseUnitOptions.includes(purchaseUnit)) setPurchaseUnit(purchaseUnitOptions[0]);
  }, [baseUnit]); // eslint-disable-line

  const submit = async () => {
    const qty = Number(String(purchaseQty).replace(",", "."));
    const pr = Number(String(price).replace(",", "."));
    if (!name.trim()) return toast.error("Укажите название ингредиента");
    if (!Number.isFinite(qty) || qty <= 0) return toast.error("Введите количество, которое вы купили");
    if (!Number.isFinite(pr) || pr <= 0) return toast.error("Введите цену");
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        base_unit: baseUnit,
        purchase_quantity: String(qty),
        purchase_unit: purchaseUnit,
        price: String(pr),
        currency_code: currency,
      };
      if (supplier.trim()) payload.supplier_name = supplier.trim();
      if (isReuse) payload.ingredient_id = line.ingredient_id;
      const resp = await quickSavePrice(payload);
      toast.success("Цена сохранена");
      onSaved(resp?.ingredient_id || null, line);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Не удалось сохранить цену");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center"
         style={{ background: "rgba(35,23,16,0.5)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
         data-testid="mo-recipe-cost-price-modal">
      <div className="w-full md:max-w-lg bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl flex flex-col"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "92dvh" }}>
        <div className="flex-shrink-0 p-5 md:p-6 pb-3">
          <div className="flex items-start justify-between mb-1">
            <div className="text-[18px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
              {isReuse ? "Обновить цену" : "Указать цену"}
            </div>
            <button onClick={onCancel} className="w-8 h-8 rounded-full grid place-items-center"
                    style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    aria-label="Закрыть" data-testid="mo-recipe-cost-price-close">
              <X size={13} />
            </button>
          </div>
          <div className="text-[12px]" style={{ color: "#8B6F4B" }}>
            Введите информацию о закупке — цена за единицу посчитается автоматически.
          </div>
        </div>

        <div className="flex-1 min-h-0 px-5 md:px-6 overflow-y-auto space-y-3">
          <div>
            <Label>Ингредиент</Label>
            {isReuse ? (
              <Field testid="mo-price-name-locked">{name}</Field>
            ) : (
              <TextInput value={name} onChange={setName} testid="mo-price-name" />
            )}
          </div>

          {!isReuse && (
            <div>
              <Label>Базовая единица (как хранить у себя)</Label>
              <div className="flex gap-1.5">
                {BASE_UNITS.map((u) => (
                  <Chip key={u} active={baseUnit === u} onClick={() => setBaseUnit(u)}
                        testid={`mo-price-base-${u}`}>
                    {u === "g" ? "граммы" : u === "ml" ? "миллилитры" : "штуки"}
                  </Chip>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label>Вы купили</Label>
            <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-2">
              <NumberInput value={purchaseQty} onChange={setPurchaseQty}
                           placeholder="напр. 100" testid="mo-price-purchase-qty" />
              <select value={purchaseUnit} onChange={(e) => setPurchaseUnit(e.target.value)}
                      className="h-10 px-3 rounded-md bg-white text-base md:text-[13px] outline-none"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-price-purchase-unit">
                {purchaseUnitOptions.map((u) => (
                  <option key={u} value={u}>
                    {u === "kg" ? "кг" : u === "g" ? "г" : u === "l" ? "л" : u === "ml" ? "мл" : "шт"}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label>За</Label>
            <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-2">
              <NumberInput value={price} onChange={setPrice}
                           placeholder="напр. 8,90" testid="mo-price-amount" />
              <Field testid="mo-price-currency">{currency}</Field>
            </div>
          </div>

          <div>
            <Label>Поставщик (необязательно)</Label>
            <TextInput value={supplier} onChange={setSupplier}
                       placeholder="напр. Metro" testid="mo-price-supplier" />
          </div>
        </div>

        <div className="flex-shrink-0 flex items-center justify-end gap-2 p-5 md:p-6 pt-3"
             style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}>
          <GhostBtn onClick={onCancel} testid="mo-price-cancel">Отмена</GhostBtn>
          <PrimaryBtn onClick={submit} disabled={saving} testid="mo-price-save">
            {saving ? "Сохранение…" : "Сохранить цену"}
          </PrimaryBtn>
        </div>
      </div>
    </div>
  );
}

/* ─────────── main drawer ─────────── */
export default function RecipeCostDrawer({ open, onClose, recipeTitle, items, onSnapshot, workbook }) {
  const [currency, setCurrency] = useState("EUR");
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // line being priced
  const [candidateFor, setCandidateFor] = useState(null); // line whose ambiguous match user picks
  const [gppFor, setGppFor] = useState(null); // line for the g↔pcs bridge editor
  const [overrides, setOverrides] = useState({}); // key = normalised recipe name, value = ingredient_id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Sanitise: trim names, drop empty-name lines, coerce empty amounts to null.
      // Empty strings crash Pydantic (422) which would surface as a false «error»
      // even though the underlying recipe simply has no saved prices yet.
      const payload = {
        items: items
          .map((it) => {
            const name = (it.name || "").trim();
            if (!name) return null;
            const key = name.toLowerCase();
            const override = overrides[key] || null;
            const amountFinite = it.amount != null && Number.isFinite(Number(it.amount));
            const amountStr = amountFinite
              ? String(Number(it.amount).toFixed(4)).replace(/0+$/, "").replace(/\.$/, "")
              : null;
            return {
              name,
              amount: amountStr && amountStr !== "" ? amountStr : null,
              unit: canonicalRecipeUnit(it.unit),
              ...(override ? { ingredient_id: override } : {}),
            };
          })
          .filter(Boolean),
        currency_code: currency,
      };
      const data = await resolveRecipeCost(payload);
      setResolved(data);
      if (onSnapshot) onSnapshot(data);
    } catch (e) {
      // Never surface a hard error — degrade to a local, client-only render
      // that mirrors the ingredients as unpriced, so the user can still tap
      // «Указать цену» on each row.
      const fallbackLines = items.map((it) => {
        const name = (it.name || "").trim();
        const canonical = canonicalRecipeUnit(it.unit);
        const hasQty = it.amount != null && Number.isFinite(Number(it.amount)) && Number(it.amount) > 0;
        return {
          name: name || "—",
          amount: hasQty ? String(it.amount) : null,
          unit: canonical,
          status: !name ? "unmatched" : (hasQty ? "unmatched" : "no_quantity"),
          ingredient_id: null,
          ingredient_name: null,
          ingredient_base_unit: null,
          candidates: [],
          price_id: null,
          unit_cost: null,
          currency_code: null,
          purchase_display: null,
          line_cost: null,
        };
      });
      setResolved({
        lines: fallbackLines,
        currency_code: currency,
        partial_total: "0.00",
        full_total: null,
        missing_count: fallbackLines.length,
        complete: false,
      });
    } finally {
      setLoading(false);
    }
  }, [items, currency, overrides]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const openPriceFor = (line, overridesForm = {}) => setEditing({ ...line, ...overridesForm });

  const onPriceSaved = async (savedIngredientId, forLine) => {
    // If the user just confirmed an ambiguous match by attaching a price to
    // an existing candidate, remember it so subsequent /resolve calls treat
    // the same recipe name as an exact match.
    if (savedIngredientId && forLine) {
      const key = (forLine.name || "").trim().toLowerCase();
      setOverrides((prev) => ({ ...prev, [key]: savedIngredientId }));
    }
    setEditing(null);
    await load();
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
           style={{ background: "rgba(35,23,16,0.5)" }}
           onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
           data-testid="mo-recipe-cost-drawer">
        <div className="w-full md:max-w-2xl bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl flex flex-col"
             style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "92dvh" }}>
          {/* header */}
          <div className="flex-shrink-0 p-5 md:p-6 pb-3">
            <div className="flex items-start justify-between mb-1">
              <div>
                <div className="text-[18px] leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  {recipeTitle || "Рецепт"}
                </div>
                <div className="text-[10.5px] tracking-[0.18em] uppercase mt-1" style={{ color: "#8B6F4B" }}>
                  Себестоимость ингредиентов
                </div>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-full grid place-items-center"
                      style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      aria-label="Закрыть" data-testid="mo-recipe-cost-close">
                <X size={13} />
              </button>
            </div>
            <div className="mt-3 flex gap-1.5 flex-wrap">
              {CURRENCIES.map((c) => (
                <Chip key={c} active={currency === c} onClick={() => setCurrency(c)}
                      testid={`mo-recipe-cost-currency-${c}`}>{c}</Chip>
              ))}
            </div>
          </div>

          {/* rows */}
          <div className="flex-1 min-h-0 px-5 md:px-6 overflow-y-auto pb-2">
            {loading && !resolved && (
              <div className="text-[13px] py-6 text-center" style={{ color: "#8B6F4B" }}>Считаем…</div>
            )}
            {resolved && resolved.lines.length === 0 && (
              <div className="text-[13px] py-6 text-center" style={{ color: "#8B6F4B" }}>
                Нет ингредиентов для расчёта.
              </div>
            )}
            {resolved && resolved.lines.length > 0
              && resolved.lines.every((l) => l.status === "unmatched" || l.status === "matched_no_price" || l.status === "possible" || l.status === "no_quantity") && (
              <div className="rounded-xl p-3 mb-2"
                   style={{ background: "#FBF3E4", color: "#5B4326" }}
                   data-testid="mo-recipe-cost-empty-hint">
                <div className="text-[13px]">Пока нет цен на ингредиенты</div>
                <div className="text-[12px] mt-0.5" style={{ color: "#8B6F4B" }}>
                  Укажите цены здесь — мы сохраним их для следующих расчётов.
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              {(resolved?.lines || []).map((line, i) => (
                <LineRow key={i}
                         line={line}
                         currency={resolved?.currency_code || currency}
                         onSetPrice={() => openPriceFor(line)}
                         onEditPrice={() => openPriceFor(line)}
                         onPickCandidate={() => setCandidateFor(line)}
                         onFixUnit={() => setGppFor(line)} />
              ))}
            </div>
          </div>

          {/* footer total */}
          {resolved && (
            <div className="flex-shrink-0 border-t border-[#EFE4D0] p-5 md:p-6 pt-3"
                 style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
                 data-testid="mo-recipe-cost-footer">
              {resolved.complete ? (
                <div className="flex items-baseline justify-between">
                  <div className="text-[13px]" style={{ color: "#5B4326" }}>Себестоимость ингредиентов</div>
                  <div className="text-[20px] tabular-nums" style={{ color: "#2A1D14", fontFamily: "'Cormorant Garamond', serif" }}
                       data-testid="mo-recipe-cost-total-full">
                    {fmtMoney(resolved.full_total)} {resolved.currency_code}
                  </div>
                </div>
              ) : (
                <div className="flex items-baseline justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[13px]" style={{ color: "#5B4326" }}>
                      Рассчитано: <span className="tabular-nums">{fmtMoney(resolved.partial_total)} {resolved.currency_code}</span>
                    </div>
                    <div className="text-[12px] mt-0.5" style={{ color: "#B26B4A" }}
                         data-testid="mo-recipe-cost-missing">
                      Не хватает данных: {resolved.missing_count}
                    </div>
                  </div>
                  <Info size={16} style={{ color: "#8B6F4B", flexShrink: 0 }} />
                </div>
              )}

              {workbook && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="mo-recipe-cost-workbook-actions">
                  {!workbook.savedEntryId ? (
                    <PrimaryBtn onClick={workbook.onSave} disabled={workbook.saving} testid="mo-recipe-cost-wb-save">
                      {workbook.saving ? "Сохранение…" : "Сохранить в Рабочую тетрадь"}
                    </PrimaryBtn>
                  ) : (
                    <>
                      <PrimaryBtn onClick={workbook.onSaveChanges} disabled={workbook.saving} testid="mo-recipe-cost-wb-save-changes">
                        {workbook.saving ? "Сохранение…" : "Сохранить изменения"}
                      </PrimaryBtn>
                      <GhostBtn onClick={workbook.onSaveVariant} testid="mo-recipe-cost-wb-save-variant">
                        Сохранить как новый вариант
                      </GhostBtn>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <QuickPriceForm line={editing} currency={currency}
                        onSaved={onPriceSaved} onCancel={() => setEditing(null)} />
      )}

      {candidateFor && (
        <CandidatePicker line={candidateFor}
                         onNew={() => { setCandidateFor(null); openPriceFor({ ...candidateFor, ingredient_id: null, ingredient_name: null, status: "unmatched" }); }}
                         onPick={async (cand) => {
                           setCandidateFor(null);
                           // Lock this recipe name to the picked ingredient; the next
                           // resolve will return the correct status (matched / matched_no_price /
                           // unit_incompatible) based on real data, without forcing the
                           // user through the price form when a price already exists.
                           const key = (candidateFor.name || "").trim().toLowerCase();
                           setOverrides((prev) => ({ ...prev, [key]: cand.ingredient_id }));
                         }}
                         onCancel={() => setCandidateFor(null)} />
      )}

      {gppFor && (
        <GppEditor line={gppFor}
                   onSaved={async () => { setGppFor(null); await load(); }}
                   onCancel={() => setGppFor(null)} />
      )}
    </>
  );
}

/* ─────────── one ingredient row ─────────── */
function LineRow({ line, currency, onSetPrice, onEditPrice, onPickCandidate, onFixUnit }) {
  const qty = fmtQty(line.amount, line.unit);
  const rowStyle = { boxShadow: "inset 0 0 0 1px #EFE4D0" };

  return (
    <div className="rounded-xl bg-white p-3" style={rowStyle} data-testid="mo-recipe-cost-line"
         data-status={line.status}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[13.5px] truncate" style={{ color: "#2A1D14" }} data-testid="mo-line-name">
            {line.name}
          </div>
          <div className="text-[11.5px] mt-0.5 tabular-nums" style={{ color: "#8B6F4B" }} data-testid="mo-line-qty">
            {line.status === "no_quantity" ? "Количество не указано" : qty}
          </div>
        </div>

        <div className="text-right">
          {line.status === "matched" && (
            <>
              <div className="text-[13.5px] tabular-nums" style={{ color: "#2A1D14" }} data-testid="mo-line-cost">
                {fmtMoney(line.line_cost)} {currency}
              </div>
              <button onClick={onEditPrice}
                      className="inline-flex items-center gap-1 text-[11px] mt-0.5"
                      style={{ color: "#8B6F4B" }}
                      data-testid="mo-line-edit">
                {line.purchase_display} <Pencil size={9} />
              </button>
            </>
          )}
          {line.status === "matched_no_price" && (
            <div className="flex flex-col items-end gap-1">
              <div className="text-[11.5px]" style={{ color: "#B26B4A" }} data-testid="mo-line-no-price">Цена не указана</div>
              <button onClick={onSetPrice}
                      className="h-8 px-3 rounded-full text-[12px]"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-line-set-price">Указать цену</button>
            </div>
          )}
          {line.status === "unmatched" && (
            <div className="flex flex-col items-end gap-1">
              <div className="text-[11.5px]" style={{ color: "#B26B4A" }} data-testid="mo-line-no-price">Цена не указана</div>
              <button onClick={onSetPrice}
                      className="h-8 px-3 rounded-full text-[12px]"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-line-set-price">Указать цену</button>
            </div>
          )}
          {line.status === "possible" && (
            <button onClick={onPickCandidate}
                    className="h-8 px-3 rounded-full text-[12px] inline-flex items-center gap-1"
                    style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-line-check-match">
              Проверьте совпадение <ChevronRight size={12} />
            </button>
          )}
          {line.status === "unit_incompatible" && (
            <button onClick={onFixUnit}
                    className="h-8 px-3 rounded-full text-[12px] inline-flex items-center gap-1"
                    style={{ background: "#F7F1E7", color: "#B26B4A", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-line-fix-unit">
              Единицы не сходятся <ChevronRight size={12} />
            </button>
          )}
          {line.status === "no_quantity" && (
            <button onClick={onSetPrice}
                    className="h-8 px-3 rounded-full text-[12px]"
                    style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-line-no-qty">Указать количество</button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────── candidate picker (for ambiguous matches) ─────────── */
function CandidatePicker({ line, onPick, onNew, onCancel }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center"
         style={{ background: "rgba(35,23,16,0.5)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
         data-testid="mo-recipe-cost-candidate-modal">
      <div className="w-full md:max-w-md bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl flex flex-col"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "82dvh" }}>
        <div className="flex-shrink-0 p-5 md:p-6 pb-3">
          <div className="flex items-start justify-between mb-1">
            <div>
              <div className="text-[16px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                «{line.name}»
              </div>
              <div className="text-[12px]" style={{ color: "#8B6F4B" }}>
                Выберите совпадение из ваших ингредиентов.
              </div>
            </div>
            <button onClick={onCancel} className="w-8 h-8 rounded-full grid place-items-center"
                    style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    aria-label="Закрыть">
              <X size={13} />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 px-5 md:px-6 overflow-y-auto space-y-1.5 pb-3">
          {line.candidates.map((c) => (
            <button key={c.ingredient_id} onClick={() => onPick(c)}
                    className="w-full rounded-xl bg-white p-3 text-left flex items-center justify-between"
                    style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-recipe-cost-candidate">
              <div>
                <div className="text-[13.5px]" style={{ color: "#2A1D14" }}>{c.name}</div>
                <div className="text-[11px]" style={{ color: "#8B6F4B" }}>Базовая единица: {c.base_unit}</div>
              </div>
              <ChevronRight size={14} style={{ color: "#8B6F4B" }} />
            </button>
          ))}
          <button onClick={onNew}
                  className="w-full rounded-xl p-3 text-left"
                  style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  data-testid="mo-recipe-cost-candidate-new">
            <div className="text-[13.5px]">Это новый ингредиент</div>
            <div className="text-[11px]" style={{ color: "#8B6F4B" }}>Создать «{line.name}» и указать цену</div>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── g↔pcs bridge / base-unit fix editor for unit_incompatible rows ─────────── */
function GppEditor({ line, onSaved, onCancel }) {
  const initialGpp = line.ingredient_grams_per_piece != null
    ? String(line.ingredient_grams_per_piece)
    : (/яйц|яйк|egg/i.test(line.ingredient_name || line.name) ? "55" : "");
  const [gpp, setGpp] = useState(initialGpp);
  const [saving, setSaving] = useState(false);

  const recipeFam = /^(g|kg)$/.test(line.unit || "") ? "mass"
    : /^(ml|l)$/.test(line.unit || "") ? "volume"
    : /^pcs$/.test(line.unit || "") ? "count" : null;
  const baseFam = line.ingredient_base_unit === "g" ? "mass"
    : line.ingredient_base_unit === "ml" ? "volume"
    : line.ingredient_base_unit === "pcs" ? "count" : null;
  const gppSupported = (recipeFam === "mass" && baseFam === "count") ||
                       (recipeFam === "count" && baseFam === "mass");

  // Any two families that are not the g↔pcs bridge fall into the "change
  // base unit + add fresh price" flow. This is destructive: the backend
  // wipes stale IngredientPrice rows when base_unit changes.
  const targetBaseUnit = recipeFam === "mass" ? "g"
    : recipeFam === "volume" ? "ml"
    : recipeFam === "count" ? "pcs" : null;
  const targetPurchaseUnit = recipeFam === "mass" ? "kg"
    : recipeFam === "volume" ? "l"
    : "pcs";
  const purchaseUnitOptions = targetBaseUnit === "g" ? ["kg", "g"]
    : targetBaseUnit === "ml" ? ["l", "ml"]
    : ["pcs"];

  const [purchaseQty, setPurchaseQty] = useState("");
  const [purchaseUnit, setPurchaseUnit] = useState(targetPurchaseUnit);
  const [price, setPrice] = useState("");

  const submitGpp = async () => {
    const v = Number(String(gpp).replace(",", "."));
    if (!Number.isFinite(v) || v <= 0) { toast.error("Введите вес одной штуки в граммах"); return; }
    if (!line.ingredient_id) { toast.error("Ингредиент не выбран"); return; }
    setSaving(true);
    try {
      await patchIngredient(line.ingredient_id, { grams_per_piece: String(v) });
      toast.success("Правило конверсии сохранено");
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Не удалось сохранить");
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const submitBaseUnitFix = async () => {
    if (!line.ingredient_id) { toast.error("Ингредиент не выбран"); return; }
    if (!targetBaseUnit) { toast.error("Единица рецепта не распознана"); return; }
    const qty = Number(String(purchaseQty).replace(",", "."));
    const pr = Number(String(price).replace(",", "."));
    if (!Number.isFinite(qty) || qty <= 0) return toast.error("Введите количество, которое вы купили");
    if (!Number.isFinite(pr) || pr <= 0) return toast.error("Введите цену");
    setSaving(true);
    try {
      // 1. Change base_unit (destructive — wipes old prices in backend).
      await patchIngredient(line.ingredient_id, { base_unit: targetBaseUnit });
      // 2. Add fresh price on the corrected base unit.
      await quickSavePrice({
        name: line.ingredient_name || line.name,
        base_unit: targetBaseUnit,
        purchase_quantity: String(qty),
        purchase_unit: purchaseUnit,
        price: String(pr),
        currency_code: line.currency_code || "EUR",
        ingredient_id: line.ingredient_id,
      });
      toast.success("Единицы исправлены");
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Не удалось сохранить");
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center"
         style={{ background: "rgba(35,23,16,0.5)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
         data-testid="mo-recipe-cost-gpp-modal">
      <div className="w-full md:max-w-md bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl flex flex-col"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "92dvh" }}>
        <div className="flex-shrink-0 p-5 md:p-6 pb-3">
          <div className="flex items-start justify-between mb-1">
            <div>
              <div className="text-[18px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                Исправить единицы
              </div>
              <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>
                «{line.ingredient_name || line.name}» хранится в {line.ingredient_base_unit || "—"},
                в рецепте — {line.unit || "—"}.
              </div>
            </div>
            <button onClick={onCancel} className="w-8 h-8 rounded-full grid place-items-center"
                    style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    aria-label="Закрыть" data-testid="mo-gpp-close">
              <X size={13} />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 px-5 md:px-6 overflow-y-auto space-y-3">
          {gppSupported ? (
            <div>
              <Label>1 шт = сколько граммов?</Label>
              <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-2 items-center">
                <NumberInput value={gpp} onChange={setGpp} placeholder="напр. 55" testid="mo-gpp-value" />
                <Field testid="mo-gpp-unit">г</Field>
              </div>
              <div className="mt-2 text-[11.5px]" style={{ color: "#8B6F4B" }}>
                Значение сохранится у ингредиента и будет применяться к нему во всех
                будущих расчётах. Это не универсальное правило — его нет для других
                ингредиентов.
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="mo-gpp-base-unit-fix">
              <div className="rounded-lg p-3" style={{ background: "#FBF3E4", color: "#5B4326" }}>
                <div className="text-[12.5px]">
                  Переключим базовую единицу с <b>{line.ingredient_base_unit || "—"}</b> на <b>{targetBaseUnit}</b>
                  {" "}и сохраним новую цену. Прежние цены на этот ингредиент будут удалены.
                </div>
              </div>
              <div>
                <Label>Вы купили</Label>
                <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-2">
                  <NumberInput value={purchaseQty} onChange={setPurchaseQty}
                               placeholder="напр. 1" testid="mo-gpp-fix-qty" />
                  <select value={purchaseUnit} onChange={(e) => setPurchaseUnit(e.target.value)}
                          className="h-10 px-3 rounded-md bg-white text-base md:text-[13px] outline-none"
                          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                          data-testid="mo-gpp-fix-unit">
                    {purchaseUnitOptions.map((u) => (
                      <option key={u} value={u}>
                        {u === "kg" ? "кг" : u === "g" ? "г" : u === "l" ? "л" : u === "ml" ? "мл" : "шт"}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label>За</Label>
                <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-2">
                  <NumberInput value={price} onChange={setPrice}
                               placeholder="напр. 0,50" testid="mo-gpp-fix-price" />
                  <Field testid="mo-gpp-fix-currency">{line.currency_code || "EUR"}</Field>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex-shrink-0 flex items-center justify-end gap-2 p-5 md:p-6 pt-3"
             style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}>
          <GhostBtn onClick={onCancel} testid="mo-gpp-cancel">Отмена</GhostBtn>
          <PrimaryBtn onClick={gppSupported ? submitGpp : submitBaseUnitFix}
                      disabled={saving} testid="mo-gpp-save">
            {saving ? "Сохранение…" : "Сохранить"}
          </PrimaryBtn>
        </div>
      </div>
    </div>
  );
}
